import styled, { keyframes } from 'styled-components';
import { Descriptions, Skeleton } from 'antd';
import { StyledColumnsWrapper, StyledManageColumns } from 'components/styledComponents/popover/Popover.styled';
import { StyledConfigRadioButton, StyledPaymentCheckbox } from 'components/reporting/Reporting.styled';
import {
  media, truncate, getBoxShadow, getZIndexes,
} from 'utils/styledHelpers';
import { StyledMoreWrapper } from 'components/styledComponents/button/Button.styled';
import { StyledIconHover } from 'shared/icon/Icon.styled';
import { StyledSelectInManageFilters } from 'components/styledComponents/select/Select.styled';
import { FilterIconWrapper, StyledFilterButton, StyledFilterButtons } from 'shared/filter/Filter.styled';

import { StyledTabWrapperWithPadding, StyledVerticalTabs } from 'components/styledComponents/tabs/Tabs.styled';

const { fullscreenModeZIndex, attachmentDetailsZIndex } = getZIndexes();

export const previewDetailsWrapperWidth = 435;

interface TruncateProps {
  $largeWidth?: boolean;
  $isCentered?: boolean;
  $isMedium?: boolean;
}

interface DescriptionListProps {
  $noPadding?: boolean;
}

interface FormItemMarginWrapperProps {
  $bigMarginBottom?: boolean;
  $hasLabelInfo?: boolean;
}

interface FormItemMarginTopWrapperProps {
  $bigMarginTop?: boolean;
  $mediumMarginTop?: boolean;
}

interface StyledMarginTopWrapperProps {
  $mediumSize?: boolean;
}

interface StyledBackWrapperProps {
  $noBorder?: boolean;
}

interface StyledSingleDescriptionListItemProps {
  $noPadding?: boolean;
  $marginBottom?: boolean;
}

interface StyledBackgroundWrapperProps {
  $isFullHeight?: boolean;
  $marginTop?: boolean;
  $noShadow?: boolean;
  $disableGrow?: boolean;
}

interface StyledBackgroundContentProps {
  $isPlayer?: boolean;
}

interface StyledBannerWrapperProps {
  $isFullWidth?: boolean;
}

interface ComponentSeparatorProps {
  $mediumSize?: boolean;
}

interface EditCreateButtonGroupProps {
  $smallPadding?: boolean;
}

interface StyledConditionItemProps {
  $isSelected?: boolean;
  $isFilled?: boolean;
}

interface StyledAnimateHeightProps {
  $maxHeight?: string;
  $hasShadow?: boolean;
}

interface StyledPreviewResultWrapperProps {
  $noMargin?: boolean;
  $noBorder?: boolean;
}

interface StyledResultNumberValueProps {
  $isMissing?: boolean;
}

interface StyledWrapperHeadingProps {
  $small?: boolean;
  $marginBottom?: string;
}

interface StyledTitleWithInfoProps {
  $noMargin?: boolean;
  $isUppercase?: boolean;
}

interface StyledConditionConfigComponentProps {
  $isWide?: boolean;
}

interface StyledMaintenancePeriodProps {
  $isInTable?: boolean;
}

interface StyledExpandableHeadingProps {
  $isOpen?: boolean;
  $disabled?: boolean;
}

interface StyledAttachmentWrapperProps {
  $mdPaddings?: boolean;
  $noPaddings?: boolean;
}

interface StyledFullScreenProps {
  $isFullScreen?: boolean;
}

interface StyledSkeletonProps {
  $marginBottom?: boolean;
  $isInline?: boolean;
  $avatarWidth?: number;
  $avatarHeight?: number;
  $titleHeight?: number;
  $titleMaxWidth?: number;
  $paragraphHeight?: number;
  $align?: string;
}

interface StyledMarginBottomProps {
  $isBig?: boolean;
}

interface StyledUploadImageWrapperProps {
    $width?: string;
    $height?: string;
}

const FormItemMarginWrapper = styled.div<FormItemMarginWrapperProps>`
  margin-bottom: ${(props) => (props.$bigMarginBottom ? '2.3rem' : '1.71rem')};
  .ant-form-item-required:after {
    display: ${(props) => (props.$hasLabelInfo ? 'none !important' : '')};
  }
`;
const FormItemMarginTopWrapper = styled.div<FormItemMarginTopWrapperProps>`
  margin-top: ${(props) => (props.$bigMarginTop && '2.85rem') || (props.$mediumMarginTop && '2.57rem') || '1.71rem'};
`;

const StyledBackWrapper = styled.div<StyledBackWrapperProps>`
  padding: 1.14rem 1.71rem;
  height: 3.57rem;
  position: relative;
  border-bottom: ${(props) => (props.$noBorder ? 'none' : `1px solid ${props.theme.colors.secondaryBorder}`)};
  border-top-right-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  border-top-left-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  color: ${(props) => props.theme.colors.primaryText};
  background: ${(props) => props.theme.colors.secondaryBg};
  box-shadow: -0px -5px 16px -10px ${(props) => props.theme.colors.secondaryShadow};
  z-index: 11;
  width: 100%;
`;

const StyledBackClick = styled.span`
  display: inline-flex;
  align-items: center;
  height: 100%;
  cursor: pointer;
  color: ${(props) => props.theme.colors.primaryText};
  > div {
    margin-right: 0.86rem;
  }
`;

const StyledTruncate = styled.div<TruncateProps>`
  display: block;
  ${truncate()};
  max-width: ${(props) => (props.$largeWidth ? '19rem' : '14rem')};
  max-height: 4.57rem;
  font-size: ${(props) => props.theme.typography.fontSizeDefault};
  margin: ${(props) => (props.$isCentered ? '0 auto' : '')};
  font-weight: ${(props) => (props.$isMedium ? props.theme.typography.fontWeightMedium : props.theme.typography.fontWeightRegular)};
  cursor: default;
`;

const DescriptionList = styled(Descriptions)<DescriptionListProps>`
  .ant-descriptions-view {
    .ant-descriptions-item-label::after {
      display: none;
    }

    .ant-descriptions-item-content {
      font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    }

    .ant-descriptions-row {
      &:last-child {
        border-bottom: ${(props) => (props.$noPadding ? 'none' : '2rem solid transparent')};
      }

      .ant-descriptions-item-container {
        margin: 0.57rem 0 0.57rem 0.14rem;
      }
    }

    .ant-descriptions-item {
      position: relative;
      padding-bottom: 0;

      &:not(:first-of-type) {
        padding-left: 1.71rem;
        padding-right: 1.71rem;

        ${media.sm`
          padding-left: 1rem;
          padding-right: 1rem;
        `}
      }

      &:last-of-type {
        padding-right: 0 !important;
      }

      &:not(:last-of-type):after {
        content: "";
        position: absolute;
        top: 0;
        right: 0;
        height: 100%;
        width: 1px;
        background-color: ${(props) => props.theme.colors.secondaryBorder};
      }

      &-label {
        color: ${(props) => props.theme.colors.filterLabelColor};
        font-size: ${(props) => props.theme.typography.fontSizeDefault};

        ${truncate()}
        span {
          ${truncate()}
        }
      }

      &-content {
        font-size: ${(props) => props.theme.typography.fontSizeDefault};
        color: ${(props) => props.theme.colors.primaryText};
      }
    }
  }
`;
const DescriptionListInPopover = styled.div`
  .ant-descriptions-view {
    .ant-descriptions-row {
      .ant-descriptions-item-container {
        margin: 0.3rem 0 0.3rem 0.14rem;
      }
    }

    .ant-descriptions-item {
      &-content {
        color: ${(props) => props.theme.colors.white};
      }
    }

    table {
      width: auto;
    }
  }
`;
const StyledViewList = styled.div`
  .ant-descriptions-item-container .ant-descriptions-item-content {
    font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    padding-right: 0.7rem;
    margin-left: 0.57rem;
    display: flex;
    max-width: 70%;
    flex: 0 0 auto;
    ${truncate()}
    span {
      ${truncate()}
    }
  }
  .ant-descriptions-item-content {
    > span,
    > div {
      font-weight: ${(props) => props.theme.typography.fontWeightMedium};
      div {
        justify-content: flex-start;
      }
    }
  }
  .ant-descriptions-row:first-child .ant-descriptions-item-container {
    display: flex;
    align-items: center;
  }
`;
const StyledSingleDescriptionListItem = styled.div<StyledSingleDescriptionListItemProps>`
  display: flex;
  align-items: center;
  padding-bottom: ${(props) => (props.$noPadding ? '0' : '2rem')};
  margin-bottom: ${(props) => (props.$marginBottom ? '0.57rem' : '0')};

  span {
    color: ${(props) => props.theme.colors.filterLabelColor};
    font-size: ${(props) => props.theme.typography.fontSizeDefault};
    ${truncate()}
  }

  p,
  ${StyledMoreWrapper} {
    font-size: ${(props) => props.theme.typography.fontSizeDefault};
    color: ${(props) => props.theme.colors.primaryText};
    font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    margin-left: 0.57rem;
  }
`;
const StyledBackgroundWrapper = styled.div<StyledBackgroundWrapperProps>`
  position: relative;
  background-color: ${(props) => props.theme.colors.secondaryBg};
  height: ${(props) => (props.$isFullHeight ? '100%' : 'auto')};
  margin-top: ${(props) => (props.$marginTop ? '1.43rem' : '0')};
  border-radius: ${(props) => `${props.theme.customBorderRadius.borderRadiusMiddle} ${props.theme.customBorderRadius.borderRadiusMiddle} 0 0`};
  padding-bottom: 4rem;
  box-shadow: ${(props) => (props.$noShadow ? 'none' : getBoxShadow(props.theme.colors.secondaryShadow))};
  display: flex;
  flex-direction: column;
  flex-grow: ${(props) => (props.$disableGrow ? 'initial' : '1')};
  form {
    padding: 0 2.85rem;
    height: auto;
    flex-grow: 1;
    background-color: ${(props) => props.theme.colors.secondaryBg};
    border-radius: ${(props) => `${props.theme.customBorderRadius.borderRadiusMiddle} ${props.theme.customBorderRadius.borderRadiusMiddle} 0 0`};
    display: flex;
    flex-direction: column;
    ${media.sm`
       padding: 0 1.14rem;
    `}
  }
  .ant-tabs {
    overflow: hidden;
  }
`;
const StyledBackgroundContent = styled.div<StyledBackgroundContentProps>`
  padding: ${(props) => (props.$isPlayer ? '0 1rem' : '0 2.85rem')};
  border-top: ${(props) => (props.$isPlayer ? 'none' : `1px solid ${props.theme.colors.secondaryBorder}`)};

  ${media.sm`
    padding: ${(props) => (props.$isPlayer ? '0' : '0 1.4rem')};
  `}
`;
const StyledNoData = styled.div`
  display: flex;
  height: 100%;
  flex-grow: 1;

  flex-direction: column;
  justify-content: center;
  > div {
    justify-content: center;
  }
`;
const StyledNoDataImg = styled.div`
  display: flex;
  svg g g g g g path:first-child {
    fill: #fff;
  }
  svg g g g > path {
    fill: #ccc;
  }
`;

const StyledNoDataText = styled.p`
  font-size: 1.14rem;
  display: flex;
  justify-content: center;
  color: ${(props) => props.theme.colors.primaryText}60;
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
`;

const StyledBannerWrapper = styled.div<StyledBannerWrapperProps>`
  width: ${(props) => (props.$isFullWidth ? '100%' : '72.4rem')};
  max-width: 100%;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  .ant-empty-normal {
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }
`;
const StyledTruncateText = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 0;
`;

const StyledTruncatedWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledBannerWrapperBg = styled(StyledBannerWrapper)`
  background-color: ${(props) => props.theme.colors.secondaryBg};
  border-radius: ${(props) => `${props.theme.customBorderRadius.borderRadiusMiddle} ${props.theme.customBorderRadius.borderRadiusMiddle} 0 0`};
  box-shadow: ${(props) => getBoxShadow(props.theme.colors.secondaryShadow)};
`;

const StyledUpdateDate = styled.div`
  position: absolute;
  top: -2rem;
  right: 0;
  z-index: 11;
  color: ${(props) => props.theme.colors.secondaryColor};
  span {
    margin-left: 8px;
  }
`;

const StyledMarginTopWrapper = styled.div<StyledMarginTopWrapperProps>`
  margin-top: 1.14rem;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  height: 100%;
`;
const ComponentSeparator = styled.hr<ComponentSeparatorProps>`
  margin: ${(props) => (props.$mediumSize ? '0 0 2.7rem' : ' 0 0 1.14rem')};
  border: none;
  width: 0;
`;
const TooltipIconWrapper = styled.div`
  cursor: pointer;
`;

const StyledViewPermissionPagesWrapper = styled.div`
  p {
    margin-bottom: 1.71rem;
  }
  > div {
    font-size: 1rem;
    margin-bottom: 2.28rem;
  }
  .ant-descriptions-view {
    padding-left: 0;
  }
`;

const StyledDeletedItem = styled.span`
  color: ${(props) => props.theme.colors.danger};
`;

const StyledMarginBottom = styled.div<StyledMarginBottomProps>`
  margin-bottom: ${(props) => (props.$isBig ? '2.28rem' : '1.14rem')};
`;

const StyledDisabledItem = styled.span`
  color: ${(props) => props.theme.colors.filterLabelColor};
`;

const StyledEmptyConfigWrapper = styled.div`
  padding: 1.71rem;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  background-color: ${(props) => props.theme.colors.primaryBg};
  text-align: center;
  margin: 0.71rem 0;
  border: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  h4 {
    font-weight: ${(props) => props.theme.typography.fontWeightMedium};
    margin-bottom: 0.57rem;
  }
`;

const EditCreateButtonGroup = styled.div<EditCreateButtonGroupProps>`
  display: flex;
  padding-top: ${(props) => (props.$smallPadding ? '1.14rem' : '4rem')};
  margin-top: auto;
  justify-content: flex-end;
  .ant-btn {
    margin-left: 1.14rem;
  }
  ${media.xs`
    flex-wrap: wrap;
    row-gap: 0.71rem;
  `}
`;

const StyledConditionItem = styled.div<StyledConditionItemProps>`
  height: 100%;
  font-size: ${(props) => props.theme.typography.fontSizeDefault};
  padding: 0 1.14rem 1rem;
  border: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  cursor: pointer;
  transition: 0.3s all;

  &:hover {
    border-color: ${(props) => props.theme.colors.blue}60;
    h6 {
      color: ${(props) => props.theme.colors.blue};
    }
  }

  ${(props) => props.$isFilled
    && `
    border-color: ${props.theme.colors.blue}80;
    background: linear-gradient(137.6deg, rgba(6, 177, 190, 0.2) 3.6%, rgba(0, 161, 179, 0.05) 95.65%);
    h6 {
      color: ${props.theme.colors.blue};
    }
  `}

  ${(props) => props.$isSelected
    && `
    background: linear-gradient(137.6deg, ${props.theme.colors.blue}40 4%,${props.theme.colors.blue}02 96%);
    border-color: ${props.theme.colors.blue}40;
    h6 {
      color: ${props.theme.colors.blue};
    }
  `}

  .ant-card-meta {
    margin: 0;
    .ant-card-meta-section {
      width: 100%;
      .ant-card-meta-title {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 1.14rem;
        border-bottom: 1px solid ${(props) => props.theme.colors.secondaryBorder};
      }
    }
  }
`;
const StyledConditionCardTitle = styled.h6`
  font-size: ${(props) => props.theme.typography.fontSizeDefault};
  padding: 1rem 0;
  transition: 0.3s color;
  ${truncate()}
`;
const StyledConditionReset = styled(StyledIconHover)`
  padding: 0.5rem;
  border-bottom: none;
`;
const StyledConditionDescription = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  &:not(:last-of-type) {
    margin-bottom: 0.3rem;
  }
`;
const StyledConditionConfigComponent = styled.div<StyledConditionConfigComponentProps>`
  max-width: ${(props) => (props.$isWide ? '800px' : '354px')};
  margin: 0 auto;
`;

const StyledMessageCloseIcon = styled.span`
  position: absolute;
  padding: 14px 16px;
  right: 0;
  top: 0;
  cursor: pointer;
`;

const animateHeight = (maxHeight?: string) => keyframes`
  to {
    max-height: ${maxHeight || '40rem'};
    opacity: 1;
  }
`;

const StyledAnimateHeight = styled.div<StyledAnimateHeightProps>`
  max-height: 0;
  opacity: 0;
  animation: ${(props) => animateHeight(props.$maxHeight)} 0.6s forwards;
  overflow: ${(props) => (props.$hasShadow ? '' : 'hidden')};
`;

const StyledPreviewResultWrapper = styled.div<StyledPreviewResultWrapperProps>`
  background: ${(props) => props.theme.colors.primaryBg};
  border: ${(props) => (props.$noBorder ? 'none' : `1px solid ${props.theme.colors.secondaryBorder}`)};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  padding: 1.29rem 1.71rem 0.72rem;
  margin-top: ${(props) => (props.$noMargin ? '0' : '2.42rem')};
`;
const StyledPreviewResultList = styled.ul`
  display: flex;
  overflow-x: auto;
  padding-bottom: 0.57rem;
`;
const StyledPreviewResultItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: 0 1.71rem;
  color: ${(props) => props.theme.colors.primaryText};
  white-space: nowrap;
  font-size: 1rem;

  &:not(:last-of-type) {
    border-right: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  }

  @media (max-width: 1200px) {
    padding: 0 1.14rem;
  }
  > div {
    display: flex;
    align-items: center;
  }
  strong {
    margin-left: 0.28rem;
    color: ${(props) => props.theme.colors.primaryText};
    white-space: nowrap;
  }
  &:first-child:before {
    position: absolute;
    content: '';
    left: 0;
    width: 1.07rem;
    border-bottom: 2px solid ${(props) => props.theme.colors.secondaryBorder};
  }
`;
const StyledPreviewResultLastItem = styled.li`
  flex-grow: 1;
  display: flex;
  justify-content: flex-end;
  gap: 1.71rem;
  position: sticky;
  right: 0;
  background: ${(props) => props.theme.colors.primaryBg};
  padding-left: 1.71rem;
`;
const StyledResultNumberValue = styled.span<StyledResultNumberValueProps>`
  padding: 0.28rem 0.57rem;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  font-size: 1rem;
  color: ${(props) => (props.$isMissing ? props.theme.colors.danger : props.theme.colors.blue)};
  background-color: ${(props) => (props.$isMissing ? `${props.theme.colors.danger}20` : `${props.theme.colors.blue}20`)};
  &:not(:last-child) {
    margin-right: 0.85rem;
  }
`;

const StyledRelativeWrapper = styled.div`
  position: relative;
`;
const StyledWrapperHeading = styled.h4<StyledWrapperHeadingProps>`
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  font-size: ${(props) => (props.$small ? props.theme.typography.fontSizeDefault : props.theme.typography.fontSizeMiddle)};
  margin-bottom: ${(props) => (props.$marginBottom ? props.$marginBottom : '0')};
`;

const StyledTitleWithInfo = styled.div<StyledTitleWithInfoProps>`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: ${(props) => (props.$noMargin ? '0' : '1.43rem')};
  font-weight: ${(props) => props.theme.typography.fontWeightMedium};
  text-transform: ${(props) => (props.$isUppercase ? 'uppercase' : 'none')};
`;

const StyledGeneralConfigWrapper = styled.div`
  margin-bottom: 1.71rem;
`;

const StyledExpandableWrapper = styled.div`
  transition: 0.3s all;
  max-height: 100rem;
  overflow: hidden;
  padding-top: 1rem;
  &.collapsed {
    max-height: 0;
    opacity: 0;
    padding-top: 0;
  }
`;

const StyledMaintenancePeriod = styled.span<StyledMaintenancePeriodProps>`
  display: flex;
  align-items: center;
  gap: 1rem;

  ${(props) => !props.$isInTable
    && `
    margin-left: 1.14rem;
    padding-left: 1.57rem;
    border-left: 1px solid ${props.theme.colors.secondaryBorder};
  `}

  & > span {
    display: flex;
    gap: 0.57rem;
  }
`;
const StyledMaintenancePeriodDates = styled.span`
  display: flex;
  align-items: center;
  gap: 0.57rem;
  margin-right: 0.57rem;
`;

const StyledSidebarInner = styled.div`
  @media (max-width: 1200px) {
    overflow-y: auto;
    padding-right: 1rem;
    height: 100%;
  }
`;

const StyledUploadImageResultWrapper = styled.div`
  position: relative;
  width: 32px;
  height: 32px;

  img {
    width: 32px;
    height: 32px;
  }
`;

const StyledSkeleton = styled(Skeleton)<StyledSkeletonProps>`
  margin-bottom: ${(props) => (props.$marginBottom ? '1.71rem' : 0)};
  width: ${(props) => (props.$isInline ? 'auto' : '100%')};
  .ant-skeleton-header {
    .ant-skeleton-avatar {
      width: ${(props) => props.$avatarWidth && `${props.$avatarWidth}px`};
      height: ${(props) => props.$avatarHeight && `${props.$avatarHeight}px`};
    }
  }

  .ant-skeleton-section {
    .ant-skeleton-title {
      height: ${(props) => props.$titleHeight && `${props.$titleHeight}px`};
      max-width: ${(props) => props.$titleMaxWidth && `${props.$titleMaxWidth}px`};
      margin-left: ${(props) => (props.$align === 'right' ? 'auto' : '0')};
    }

    .ant-skeleton-paragraph li {
      height: ${(props) => props.$paragraphHeight && `${props.$paragraphHeight}px`};
    }
  }
`;

const StyledExpandableReports = styled.div`
  margin: 2.28rem 0;
`;
const StyledExpandableReportItem = styled.div`
  margin-bottom: 1rem;
  box-shadow: ${(props) => getBoxShadow(props.theme.colors.secondaryShadow)};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};

  .ant-table-content:hover::-webkit-scrollbar {
    height: 6px;
  }
`;
const StyledExpandableHeading = styled.div<StyledExpandableHeadingProps>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  column-gap: 0.57rem;
  background-color: ${(props) => props.theme.colors.primaryBg};
  padding: 0 1.71rem 0 1.32rem;
  height: 4.57rem;
  cursor: ${(props) => (props.$disabled ? 'auto' : 'pointer')};
  opacity: ${(props) => (!props.$isOpen && props.$disabled ? '0.6' : '1')};
  color: ${(props) => props.theme.colors.primaryText};

  ${(props) => props.$isOpen
    && `
      border-radius: ${props.theme.customBorderRadius.borderRadiusMiddle} ${props.theme.customBorderRadius.borderRadiusMiddle} 0 0;
  `}
`;
const StyledExpandableHeadingLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 0.71rem;

  svg path {
    fill: ${(props) => props.theme.colors.secondaryColor};
  }
`;
const StyledExpandableReportInner = styled.div`
  padding: 1.87rem 2.42rem 2.87rem;
  border-radius: ${(props) => `0 0 ${props.theme.customBorderRadius.borderRadiusMiddle} ${props.theme.customBorderRadius.borderRadiusMiddle}`};

  .ant-tabs .ant-tabs-tab {
    height: 2.85rem;
  }
`;
const StyledExpandableAggregationDate = styled.time`
  display: block;
  font-size: ${(props) => props.theme.typography.fontSizeSmall};
  color: ${(props) => props.theme.colors.secondaryColor};
  text-align: right;
  margin-top: 0.71rem;
`;

const StyledAttachmentWrapper = styled.div<StyledAttachmentWrapperProps>`
  height: 100%;
  padding: ${(props) => (props.$mdPaddings ? '0 1.2rem' : ' 2.4rem 3rem 0')};

  ${media.sm`
    padding: ${(props) => (props.$mdPaddings || props.$noPaddings ? '0' : ' 2rem 1.14rem 0')};      
  `}

  ${({ $noPaddings }) => $noPaddings
    && `
      padding: 0;
  `}
  
  .ant-list-item {
    border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusMiddle};
  }
  .ant-list-grid .ant-col > .ant-list-item {
    margin-bottom: 0;
  }

  ul.ant-card-actions li {
    display: flex;
    margin-left: 5.5rem;
    @media (max-width: 1200px) {
      margin-left: 0;
    }
  }
  .ant-card-meta-section {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
  }
  .ant-card,
  .ant-card-meta-description {
    display: flex;
    flex-direction: column;
  }
  .ant-card-body,
  .ant-card-meta {
    flex: 1;
    overflow: hidden;
  }
  .ant-card-meta {
    margin: 0;
  }
  .ant-list-grid .ant-col {
    height: 100%;
    display: flex;
  }
  .ant-list-grid .ant-col > .ant-list-item {
    flex-grow: 1;
  }
  .ant-empty-normal {
    margin: 15rem 0;
  }
  .ant-card-cover .ant-image {
    width: 100%;
    height: 10.5rem;
    min-height: 10.5rem;
    max-height: 10.5rem;
    border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusDefault};
    background-color: ${(props) => props.theme.colors.primaryBg};
    margin-bottom: 1.14rem;
    position: relative;
    z-index: 1;
    cursor: pointer;
    > img {
      border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusDefault};
      width: 100%;
      height: 10.5rem;
      object-fit: cover;
    }

    &.pdf-image {
      display: flex;
      align-items: center;
      justify-content: center;
      img {
        width: 60px;
        height: auto;
      }
    }

    .ant-image-mask-info {
      color: #fff;
      svg {
        color: #fff;
      }
    }
  }
  .ant-card-bordered .ant-card-cover {
    margin: 0;
  }
  .ant-card-meta-description > div:not(:first-child) {
    font-size: 1rem;
    display: flex;
    align-items: center;
    font-weight: ${(props) => props.theme.typography.fontWeightRegular};
    color: ${(props) => props.theme.colors.primaryText};
  }
  .ant-card-meta-description > div:last-child {
    margin-bottom: 0;
  }
  & .ant-card-bordered {
    border: none;
  }
  .ant-card-body {
    border-top: none;
    border-radius: ${(props) => `0 0 ${props.theme.customBorderRadius.borderRadiusSmall} ${props.theme.customBorderRadius.borderRadiusSmall}`};
  }
  & .ant-list-item .ant-card-cover {
    border-radius: ${(props) => `${props.theme.customBorderRadius.borderRadiusSmall} ${props.theme.customBorderRadius.borderRadiusSmall} 0 0`};
    padding: 1.14rem 1.28rem 0;
    @media (max-width: 1280px) {
      min-height: auto;
    }
    > div img {
      vertical-align: middle;
    }
  }
`;

const StyledAttachmentPreviewWrapper = styled.div`
  display: flex;
  &:not(.no-details) {
    .ant-image-preview-body,
    .ant-image-preview-footer {
      max-width: calc(100% - (${previewDetailsWrapperWidth}px + 40px));
      margin: 0 20px;
      ${media.sm`
        max-width: 100%;
        max-height: calc(100dvh - 255px);
      `}
    }

    .ant-image-preview-close {
      right: calc(${previewDetailsWrapperWidth}px + 10px);
      ${media.sm`
          right: 10px;
      `}
    }
  }

  .ant-image-preview-body {
    iframe {
      width: 1000px;
      max-width: 80vw;
      height: 600px;
      max-height: 80vh;

      ${media.sm`
        width: 100%;
        max-width: calc(100vw - 16px);
     `}
    }
    video {
      width: 1000px;
      max-width: 100%;
    }
  }

  .ant-image-preview-close {
    background-color: rgba(0, 0, 0, 0.25);
    top: 60px;
  }

  &.pdf {
    .ant-image-preview-actions {
      display: none;
    }
    .ant-image-preview-img {
      background: ${(props) => props.theme.colors.primaryBg};
      padding: 4rem;
      border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
    }
  }

  .ant-image-preview-img {
    max-height: calc(100% - 8rem);
    border-radius: 0;
  }

  .ant-image-preview-mask {
    height: calc(100% - 3.43rem);
    top: 3.43rem;
  }

  .ant-image-preview-actions {
    height: 3.43rem;
    background-color: rgba(38, 38, 38, 0.85);
    padding-right: 1.71rem;

    ${media.sm`
      padding-right: 1.4rem;
      
      &-progress {
        display: flex;
        align-items: center;
        left: 1.4rem;
        transform: none;
        padding: 0.32rem;
        height: 2.14rem;
        background-color: rgba(0, 0, 0, 0.5);
        border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
      }
    `}
  }

  .ant-image-preview-switch-next {
    right: calc(${previewDetailsWrapperWidth}px + 10px);
  }
  .ant-image-preview-switch-next,
  .ant-image-preview-switch-prev {
    background-color: rgba(0, 0, 0, 0.25);
    transition: 0.2s opacity;
    svg {
      color: #fff;
    }
    &-disabled {
      opacity: 0.6;
    }
  }
`;
const StyledAttachmentPreviewDetails = styled.div`
  position: fixed;
  top: 3.43rem;
  right: 0;
  z-index: ${attachmentDetailsZIndex};
  width: ${previewDetailsWrapperWidth}px;
  height: 100%;
  background-color: ${(props) => props.theme.colors.secondaryBg};
  padding: 2.85rem;
  opacity: 0;

  animation: scale 0.3s forwards;
  animation-delay: 0.15s;
  @keyframes scale {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  ${media.sm`
    width: 100%;
    height: auto;
    bottom: 0;
    top: unset;
    max-height: 255px;
    overflow: auto;
  `}
`;

const StyledFullScreenToggle = styled.div<StyledFullScreenProps>`
  padding-left: 1rem;
  margin-left: 1rem;
  margin-bottom: 0.4rem;
  border-left: 1px solid ${(props) => props.theme.colors.primaryBorder};
  cursor: pointer;

  ${(props) => props.$isFullScreen
    && `
    padding-right: 1rem;  
    border-right: 1px solid ${props.theme.colors.primaryBorder};
  `}

  svg {
    path {
      transition: 0.3s all;
      fill: ${(props) => props.theme.colors.black}90;
    }
  }

  &:hover svg path {
    fill: ${(props) => props.theme.colors.blue};
  }
`;

const StyledFullscreenContainer = styled.div<StyledFullScreenProps>`
  ${(props) => props.$isFullScreen
    && `
    position: fixed;
    top: 0;
    left: 0;
    width: 100dvw;
    height: 100dvh;
    background: ${props.theme.colors.secondaryBg};
    z-index: ${fullscreenModeZIndex};
    padding: 0 1.14rem 5rem;
    overflow: auto;
  `};
`;

const StyledFullscreenActions = styled.div<StyledFullScreenProps>`
  ${(props) => props.$isFullScreen
    && `
    display: flex;
    align-items: center;
    flex-direction: row-reverse;
    position: sticky;
    z-index: ${fullscreenModeZIndex};
    top: 0;
    background-color: ${props.theme.colors.secondaryBg};
    width: 100%;
    height: 40px;
    
    & > ${StyledManageColumns} {
      margin-top: 0;
    }
    
    & ${StyledFilterButtons},
    & ${StyledColumnsWrapper},
    & ${StyledConfigRadioButton},
    & ${StyledSelectInManageFilters},
    & ${StyledFullScreenToggle},
    & ${StyledPaymentCheckbox} {
      margin-top: 0;
      margin-bottom: 0;
    }
    
    ${StyledFilterButton} {
      .ant-btn {
        min-width: unset !important;
        margin-left: 0.8rem;
      }
      ${FilterIconWrapper} + span {
        display: none;
      }
    }
  `}
`;

const StyledGrayMessagesWrapper = styled.ul`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

const StyledGrayMessageItem = styled.li`
  display: inline-flex;
  padding: 0 0.57rem;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  background: ${(props) => props.theme.colors.tableTrHoverColor};
  line-height: 1.57rem;
`;

// user managament styled

interface StyledUMWrapperWithPaddingProps {
  $borderTop?: boolean;
}
interface StyledPGButtonActionsProps {
  $disable?: boolean;
  $radius?: boolean;
  $assigned?: boolean;
  $isView?: boolean;
  $isTemporary?: boolean;
  $isFromGroup?: boolean;
  $allowSelect?: boolean;
}
interface StyledUsersTruncateProps {
  $permissionReport?: boolean;
  $isMargin?: boolean;
  $isPadding?: boolean;
}
interface StyledPGActionsProps {
  $disable?: boolean;
  $assigned?: boolean;
}

const StyledUMWrapperWithPadding = styled(StyledTabWrapperWithPadding)<StyledUMWrapperWithPaddingProps>`
  border: none;
  border-top: 1px solid ${(props) => (props.$borderTop ? props.theme.colors.secondaryBorder : 'none')};
  border-top-left-radius: ${(props) => (props.$borderTop ? 'unset' : props.theme.customBorderRadius.borderRadiusMiddle)};
  border-top-right-radius: ${(props) => (props.$borderTop ? 'unset' : props.theme.customBorderRadius.borderRadiusMiddle)};
  padding: 3.2rem 2.85rem 0;
  .ant-select-multiple .ant-select-selection-search {
    margin-left: 1.5rem;
  }

  ${media.sm`
    padding: 1.14rem;
  `}
`;

const StyledCreateEditUser = styled.div`
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  position: relative;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  .ant-tabs-left > .ant-tabs-nav .ant-tabs-nav-list {
    flex: initial;
  }
`;

const StyledPGActions = styled.button<StyledPGActionsProps>`
  display: inline-flex;
  width: auto;
  height: 2rem;
  background: transparent;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  color: ${(props) => props.theme.colors.primaryText};
  line-height: 2rem;
  padding: 0;
  max-width: 18rem;
  outline: none;
  border: none;
  font-weight: ${(props) => props.theme.typography.fontWeightRegular};
  &:hover,
  &:active,
  &:focus {
    background: transparent;
    color: ${(props) => (props.$disable ? props.theme.colors.secondaryColor : `${props.$assigned ? props.theme.colors.white : props.theme.colors.primaryText}`)};
  }
  ${truncate()};
  > span {
    max-width: 100%;
    font-size: 1rem;
    ${truncate()};
  }
`;

const StyledPGButtonActions = styled(StyledPGActions)<StyledPGButtonActionsProps>`
  opacity: ${(props) => (props.$allowSelect && props.$disable && props.$assigned ? '0.5' : '1')};
  margin: ${(props) => (props.$disable && props.$assigned && '0.43rem 0.5rem 0.43rem 0') || (props.$disable && '0.43rem 0.71rem 0.43rem 0') || '0.43rem 0.5rem 0.43rem 0'};
  padding: ${(props) => (props.$isView && '0 1.14rem 0 0.4rem') || '0 1.14rem'};
  cursor: ${(props) => (props.$disable ? 'not-allowed' : 'pointer')};
  user-select: ${(props) => (props.$disable ? 'none' : 'inherit')};
  border-radius: ${(props) => (props.$assigned && `${props.theme.customBorderRadius.borderRadiusSmall}`)
    || (props.$isView && props.$radius && `${props.theme.customBorderRadius.borderRadiusSmall} 0 0 ${props.theme.customBorderRadius.borderRadiusSmall}`)
    || `${props.theme.customBorderRadius.borderRadiusSmall}`};
  width: auto;

  &,
  &:hover,
  &:active,
  &:focus {
    background: ${(props) => (props.$isTemporary && !props.$isFromGroup && `${props.theme.colors.blue}20`)
      || (props.$isView && 'transparent')
      || `${(props.$assigned && props.theme.colors.blue) || props.theme.colors.primaryBg}`};
    color: ${(props) => (props.$isTemporary && !props.$isFromGroup && props.theme.colors.blue)
      || (props.$isView && props.theme.colors.secondaryColor)
      || `${(props.$assigned && props.theme.colors.white) || props.theme.colors.primaryText}`};
  }
  &:not(:last-of-type) {
    border-right: 1px solid ${(props) => (props.$isView ? props.theme.colors.secondaryBorder : 'transparent')};
  }
  &:first-child {
    padding-left: ${(props) => (props.$isView && !props.$assigned ? '0.4rem' : '1.14rem')};
  }
`;
const StyledPGButtonActionsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -0.43rem 0;
`;

const StyledUserManagementTable = styled.div`
  .user-expanded-row {
    background: ${(props) => props.theme.colors.tableTrEvenColor};
    & + .ant-table-expanded-row > td,
    tr.ant-table-expanded-row:hover > td {
      background: ${(props) => props.theme.colors.tableTrEvenColor};
      .ant-table-wrapper {
        width: 100%;
        margin: 0 auto;
        border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
        padding: 1px;
      }
    }
    &,
    & > td {
      border-bottom: 1px solid ${(props) => props.theme.colors.tableTrEvenColor} !important;
    }
  }
  tr.ant-table-row.collapsed-row:hover > td {
    background: ${(props) => props.theme.colors.primaryBg}60!important;
  }
  tr.ant-table-row.user-expanded-row:hover > td {
    background: initial !important;
  }

  .ant-table .ant-table-tbody > tr:not(.ant-table-expanded-row) > td:not(.custom-checkbox):last-child.user-collapsed-checkbox {
    & > label.ant-checkbox-wrapper {
      position: relative;
    }
  }
  .ant-table-content {
    margin-bottom: -1px;
    table {
      table-layout: fixed !important;
      border-collapse: collapse;
    }
  }

  .ant-table {
    tr.ant-table-expanded-row {
      .ant-table-wrapper {
        border: 1px solid ${(props) => props.theme.colors.secondaryBorder};
        border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
        overflow: hidden;
      }
      .ant-table .permission-group-row {
        border: none;
      }
    }
    .ant-table-tbody > tr {
      border-bottom: none;
      & > td {
        border-bottom: 1px solid ${(props) => props.theme.colors.secondaryBorder};
      }
    }
    .ant-table.ant-table-middle .ant-table .ant-table-tbody > tr {
      &.permission-row > td {
        background: ${(props) => props.theme.colors.secondaryBg};
      }
    }
  }

  .ant-table.ant-table-middle .ant-table .ant-table-tbody > tr {
    &.permission-group-row > td.custom-checkbox:last-child,
    &.permission-row > td.edit-action {
      padding-right: calc(1.15rem - 2px);
    }
  }
  .ant-table .permission-group-row,
  .ant-table .permission-row {
    background: ${(props) => props.theme.colors.secondaryBg};
  }
  .ant-table.ant-table-middle .ant-table-tbody > tr > td.ant-table-cell,
  .ant-table.ant-table-middle .ant-table-tbody > tr > td:not(.ant-table-row-expand-icon-cell) {
    padding: 0.7rem 0;
  }
  .ant-table .ant-table-tbody > tr:not(.ant-table-expanded-row) > td:not(.custom-checkbox):last-child.user-collapsed-checkbox {
    padding-right: 2.28rem;
  }
  .ant-table .ant-table-tbody > tr {
    &.permission-row:last-child > td,
    &:last-child:not(.ant-table-expanded-row):not(.partners-brands-row):not(.permission-row):not(.collapsed-row) > td {
      border-bottom: 0;
    }
  }
  .ant-table-container {
    border-top-right-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  }

  ${media.sm`
    .ant-table-expand-icon-col {
      width: 36px;
    }
    .ant-table.ant-table-small .ant-table-tbody > tr > td.ant-table-row-expand-icon-cell + td {
      padding-left: 10px;
    }
  `}
`;

const MStyledPGInner = styled.div`
  border: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  background-color: ${(props) => props.theme.colors.secondaryBg};
  margin: 0.4rem 0;
`;
const MStyledPGItemWrapper = styled.div`
  padding: 1.14rem 0.71rem;

  &:not(:last-child) {
    border-bottom: 1px solid ${(props) => props.theme.colors.secondaryBorder};
  }

  > strong {
    display: inline-block;
    margin-bottom: 0.57rem;
  }
`;

const StyledPermissionsWrapper = styled.div`
  padding: 0 1.14rem 1.14rem;
`;

const StyledUsersTruncate = styled(StyledTruncate)<StyledUsersTruncateProps>`
  max-width: ${(props) => (props.$permissionReport ? '30rem' : '56rem')};
  text-align: left;
  margin-left: ${(props) => (props.$permissionReport || props.$isPadding ? '0' : '1rem')};
  padding-left: ${(props) => (props.$permissionReport || props.$isMargin ? '0' : '1.3rem')};
  @media (max-width: 1600px) {
    max-width: 30rem;
  }
  @media (max-width: 1366px) {
    max-width: 15rem;
  }
`;

const StyledPermissionLevelCell = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-right: 1.71rem;
`;
const StyledPermissionLevel = styled.div`
  display: flex;
  align-items: center;
  gap: 0.57rem;
`;

const LeftAlignedText = styled.div`
  text-align: left;
`;
const StyledPerferencesItem = styled.div`
  border-left: 2px solid ${(props) => props.theme.colors.blue};
  padding-left: 0.71rem;
`;

const StyledUploadImageWrapper = styled.div<StyledUploadImageWrapperProps>`
  img {
    width: ${(props) => (props.$width ? `${props.$width}px` : '100%')};
    height: ${(props) => (props.$height ? `${props.$height}px` : '100%')};
    object-fit: contain;
    border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
    vertical-align: middle;
  }
  h3 {
    margin-bottom: 0.57rem;
    font-size: 1rem;
  }
  > div {
    display: flex;
    justify-content: center;
    position: relative;
    background: ${(props) => props.theme.colors.grayButton};
    border: 1px solid #c2c6cb;
    border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
    height: 9.28rem;
  }
  &:hover {
    div {
      opacity: 1;
    }
  }
  div {
    transition: all 0.2s ease-out;
  }
`;
const StyledImgWrapper = styled.div`
  position: relative;
`;

const StyledImageRemoveWrapper = styled.div`
  position: absolute;
  top: 0.85rem;
  right: 0.85rem;
  cursor: pointer;
  width: 2rem;
  height: 2rem;
  border-radius: ${(props) => props.theme.customBorderRadius.borderRadiusSmall};
  background: ${(props) => props.theme.colors.grayButton};
  svg path {
    fill: ${(props) => props.theme.colors.blue};
  }
  display: flex;
  opacity: 0;
  align-items: center;
  justify-content: center;
`;

export {
  ComponentSeparator,
  FormItemMarginWrapper,
  FormItemMarginTopWrapper,
  StyledMarginTopWrapper,
  StyledNoData,
  StyledNoDataImg,
  StyledNoDataText,
  StyledBackWrapper,
  StyledTruncate,
  DescriptionList,
  DescriptionListInPopover,
  StyledBackgroundWrapper,
  StyledBackgroundContent,
  StyledBannerWrapperBg,
  StyledBannerWrapper,
  StyledBackClick,
  StyledTruncateText,
  StyledTruncatedWrapper,
  StyledUpdateDate,
  StyledViewList,
  StyledSingleDescriptionListItem,
  TooltipIconWrapper,
  StyledViewPermissionPagesWrapper,
  StyledDeletedItem,
  StyledDisabledItem,
  StyledEmptyConfigWrapper,
  EditCreateButtonGroup,
  StyledConditionItem,
  StyledConditionCardTitle,
  StyledConditionReset,
  StyledConditionDescription,
  StyledConditionConfigComponent,
  StyledMessageCloseIcon,
  StyledAnimateHeight,
  StyledPreviewResultWrapper,
  StyledPreviewResultList,
  StyledPreviewResultItem,
  StyledPreviewResultLastItem,
  StyledResultNumberValue,
  StyledRelativeWrapper,
  StyledWrapperHeading,
  StyledExpandableWrapper,
  StyledTitleWithInfo,
  StyledGeneralConfigWrapper,
  StyledMaintenancePeriod,
  StyledMaintenancePeriodDates,
  StyledSidebarInner,
  StyledUploadImageResultWrapper,
  StyledSkeleton,
  StyledExpandableReports,
  StyledExpandableReportItem,
  StyledExpandableHeading,
  StyledExpandableHeadingLeft,
  StyledExpandableReportInner,
  StyledExpandableAggregationDate,
  StyledAttachmentWrapper,
  StyledAttachmentPreviewWrapper,
  StyledAttachmentPreviewDetails,
  StyledFullScreenToggle,
  StyledFullscreenContainer,
  StyledFullscreenActions,
  LeftAlignedText,
  StyledGrayMessagesWrapper,
  StyledGrayMessageItem,
  StyledCreateEditUser,
  StyledVerticalTabs,
  StyledPGActions,
  StyledUserManagementTable,
  MStyledPGInner,
  MStyledPGItemWrapper,
  StyledUsersTruncate,
  StyledUMWrapperWithPadding,
  StyledPGButtonActions,
  StyledPGButtonActionsWrapper,
  StyledPermissionsWrapper,
  StyledPermissionLevelCell,
  StyledPermissionLevel,
  StyledPerferencesItem,
  StyledMarginBottom,
  StyledUploadImageWrapper,
  StyledImgWrapper,
  StyledImageRemoveWrapper,
};
